import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/montastic')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Montastic Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Montastic: Montastic checks every 1-5 minutes and has a free plan. Uptrack checks every 30 seconds, free for 5 monitors, paid from $3/mo." },
      { property: 'og:title', content: 'Montastic Alternative — Uptrack' },
      { property: 'og:description', content: "Compare Uptrack and Montastic on check intervals, plans, alert channels, and status pages." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-montastic.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/montastic' },
    ],
    scripts: comparisonScripts('montastic', "Montastic"),
  }),
})

const data: ComparisonData = {
  competitor: "Montastic",
  tagline: "Montastic is a simple uptime monitor with a free plan of 50 monitors at 5-minute checks and paid plans from $5/mo. Uptrack checks every 30 seconds on every plan, from $3/mo.",
  heroDescription: "These facts come from Montastic's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "50 monitors at 5 min" },
    { feature: "Cheapest paid plan", uptrack: "Pro $3/mo: 50 monitors at 30s", competitor: "Team $5/mo: 15 monitors at 1 min", uptrackWins: true },
    { feature: "50 monitors on a paid plan", uptrack: "$3/mo (Pro)", competitor: "$10/mo (Business)", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "1 min", uptrackWins: true },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Included on all plans" },
  ],

  painPoints: [
    {
      title: "Check interval",
      description: "Montastic's free plan checks every 5 minutes and its paid plans every minute. Uptrack checks every 30 seconds on every plan.",
    },
    {
      title: "Price for 50 monitors",
      description: "Montastic Business is $10/mo for 50 monitors at 1-minute checks. Uptrack Pro is $3/mo for 50 monitors at 30-second checks.",
    },
    {
      title: "Browser flows on the entry plan",
      description: "Uptrack Pro includes 5 Playwright browser monitors for $3/mo. Each run follows a flow such as login or checkout and saves a screenshot at each step, so you see which step broke.",
    },
  ],

  differentiators: [
    "30-second checks on every plan, compared with a 1-minute minimum on Montastic.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Alert channels: Email, Slack, Discord, Telegram, and webhooks.",
    "Escalation policies on Team and Business route an unacknowledged alert to the next person after a delay you set.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need more than 5 free monitors, Montastic offers 50 monitors at 5-minute checks. Uptrack's free plan is 5 monitors at 30 seconds from one location.",
    "If you need SMS alerts, Montastic Business includes 50 SMS.",
    "If you only need an email when a URL stops responding, Montastic keeps setup minimal.",
  ],
}
